"use client";

import { Box, Stack } from "@chakra-ui/react";
import { formatScore } from "@zerotofinals/utils";
import type { FC, ReactElement } from "react";
import Tooltip from "./Tooltip";
import { SkeletonBox } from "./Skeleton";

type Props = {
  isLoading?: boolean;
  tooltipTitle?: string;
  segments: {
    id: string;
    color: string;
    percentage: number;
    label: string;
  }[];
};

const ProgressBar: FC<Props> = ({ isLoading, tooltipTitle, segments }) => {
  const renderedSegments = segments.reduce(
    (result, { id, color, percentage }, index) => {
      const previousWidth = result.totalWidth;
      const updatedSegments = [...result.segments];

      const segment = (
        <Box
          data-id={id}
          key={color}
          position="absolute"
          h="100%"
          w={`${percentage}%`}
          left={`${previousWidth}%`}
          bg={color}
          zIndex={segments.length - index}
        />
      );

      updatedSegments.push(segment);

      return {
        segments: updatedSegments,
        totalWidth: result.totalWidth + percentage,
      };
    },
    { segments: [] as ReactElement[], totalWidth: 0 },
  ).segments;

  if (isLoading) {
    return <SkeletonBox h="15px" />;
  }

  return (
    <Tooltip
      title={tooltipTitle}
      showArrow
      content={
        <Stack as="ul" gap={1}>
          {segments.map(({ id, percentage, label }) => (
            <li key={id}>
              <b>{formatScore(percentage)}%</b> &mdash; {label}
            </li>
          ))}
        </Stack>
      }
    >
      <Box
        position="relative"
        h="15px"
        w="100%"
        ml="-1px"
        bg="#edf2f7"
        borderRadius="2px"
        overflow="hidden"
      >
        {renderedSegments}
      </Box>
    </Tooltip>
  );
};

export default ProgressBar;
